import { List, Map } from 'immutable';

import { getTimeRangeStart, getToday, patchTimeRanges } from './time';
import { getDaysDifference, getDateKey } from '../time';
import { shapeId, shapeLinkedShops } from '../listings/listings';
import { getSize } from '../iterable/getSize';
import { get } from '../iterable/get';

import { EMPTY, VELA } from '../../constants';
import { LISTING } from '../../constants/listings';

export function shapeScheduledListingsForApp({ response, state, userShops }) {
  function shapeProduct(date, update, listing) {
    const shopIds = List(listing.merged_products_shop_ids);
    const { linkedProductsShopIds, linkedProductsShopChannels } = shapeLinkedShops({ shopIds, userShops });

    return Map({
      date,
      updateId: shapeId(update.id),
      updateStatus: update.status,
      [LISTING[VELA].CREATED_AT]: update.created_at,
      [LISTING[VELA].LINKED_PRODUCTS_SHOP_CHANNELS]: linkedProductsShopChannels,
      [LISTING[VELA].LINKED_PRODUCTS_SHOP_IDS]: linkedProductsShopIds,
      [LISTING[VELA].PHOTO]: listing.thumbnail,
      [LISTING[VELA].PRODUCT_ID]: shapeId(listing.id),
      [LISTING[VELA].SCHEDULED_AT]: update.scheduled_at,
      [LISTING[VELA].TITLE]: listing.title,
    });
  }

  const scheduledUpdates = get('scheduled_updates')(response);

  if (getSize(scheduledUpdates) === 0) {
    const loaded = state.getIn(['updates', 'loaded'], 0);
    return state
      .setIn(['updates', 'loaded'], loaded)
      .setIn(['updates', 'total'], loaded);
  }

  const today = getToday();

  function reduceScheduledUpdates(result, scheduledUpdate) {
    const { id, listings, scheduled_at: scheduledAt } = scheduledUpdate;
    const updateId = shapeId(id);
    const dateKey = getDateKey(scheduledAt);

    function updateLoaded(loaded = 0) {
      return loaded + 1;
    }

    if (getSize(listings) === 0) {
      return result.updateIn(['updates', 'loaded'], updateLoaded);
    }

    const { days } = getDaysDifference(today, new Date(scheduledAt), 0);
    const updateRange = getTimeRangeStart(today, days);
    const updateRangeKey = getDateKey(updateRange);

    function updateDates(dates = EMPTY.MAP) {
      if (dates.hasIn(['ranges', dateKey])) {
        return dates;
      }

      function addDateKey(keys = EMPTY.LIST) {
        return keys.push(dateKey);
      }

      function addRange(ranges = EMPTY.MAP) {
        return ranges.set(dateKey, updateRangeKey);
      }

      function addTimestamp(timestams = EMPTY.MAP) {
        return timestams.set(dateKey, scheduledAt);
      }

      return dates
        .update('keys', addDateKey)
        .update('ranges', addRange)
        .update('timestamps', addTimestamp);
    }

    function updateRanges(ranges = EMPTY.MAP) {
      if (ranges.hasIn(['timestamps', updateRangeKey])) {
        return ranges;
      }

      function addRangeKey(keys = EMPTY.LIST) {
        return keys.push(updateRangeKey);
      }

      function addTimestamp(timestamps = EMPTY.MAP) {
        return timestamps.set(updateRangeKey, updateRange);
      }

      return ranges
        .update('keys', addRangeKey)
        .update('timestamps', addTimestamp);
    }

    function updateProducts(products = EMPTY.MAP) {
      function reduceListings(reducedListings, listing) {
        const productId = shapeId(listing.id);

        function addProductId(productsByDate = EMPTY.LIST) {
          return productsByDate.push(productId);
        }

        function updateProductsById(productsById = EMPTY.MAP) {
          return productsById.set(productId, shapeProduct(dateKey, scheduledUpdate, listing));
        }

        return reducedListings
          .updateIn(['byDate', dateKey], addProductId)
          .update('byId', updateProductsById);
      }

      return listings.reduce(reduceListings, products.delete('processing'));
    }

    function updateUpdates(updates = EMPTY.MAP) {
      function reduceListingsToIds(products, listing) {
        const productId = shapeId(listing.id);
        return products.push(productId);
      }

      const update = Map({
        scheduledAt,
        products: listings.reduce(reduceListingsToIds, EMPTY.LIST),
      });

      function addUpdateId(updatesByDate = EMPTY.LIST) {
        return updatesByDate.push(updateId);
      }

      if (updates.hasIn(['byDate', dateKey])) {
        return updates
          .setIn(['byId', updateId], update)
          .update('loaded', updateLoaded)
          .updateIn(['byDate', dateKey], addUpdateId);
      }

      return updates
        .setIn(['byId', updateId], update)
        .update('loaded', updateLoaded)
        .updateIn(['byDate', dateKey], addUpdateId);
    }

    return result
      .update('dates', updateDates)
      .update('products', updateProducts)
      .update('ranges', updateRanges)
      .update('updates', updateUpdates);
  }

  return patchTimeRanges(scheduledUpdates.reduce(reduceScheduledUpdates, state));
}
